import React, { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Ticket, ListTodo, Mail, Clock, TrendingUp, Hourglass, CheckCircle2, ArrowRight, Flame, X, Sparkles, Loader2, RefreshCw, Link2, Copy, ExternalLink, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCompany } from "../context/CompanyContext";
import { OnboardingChecklist } from "../components/OnboardingChecklist";

const PERIODS = [["day", "Aujourd'hui"], ["month", "Ce mois"], ["year", "Cette année"]];

function inPeriod(iso, period, range) {
  if (!iso) return false;
  const d = new Date(iso);
  if (range && (range.from || range.to)) {
    if (range.from && d < new Date(range.from)) return false;
    if (range.to) { const end = new Date(range.to); end.setHours(23, 59, 59, 999); if (d > end) return false; }
    return true;
  }
  const n = new Date();
  if (period === "day") return d.toDateString() === n.toDateString();
  if (period === "month") return d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear();
  return d.getFullYear() === n.getFullYear();
}

export default function HomePage() {
  const { user } = useAuth();
  const { selectedId } = useCompany();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [emails, setEmails] = useState([]);
  const [period, setPeriod] = useState("month");
  const [range, setRange] = useState({ from: "", to: "" });
  const [copilot, setCopilot] = useState(null);
  const [copilotLoading, setCopilotLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const me = user?.id;

  useEffect(() => { api.get("/companies").then((r) => setCompanies(r.data || [])).catch(() => {}); }, []);

  const loadCopilot = useCallback(async () => {
    setCopilotLoading(true);
    try {
      const cq = selectedId && selectedId !== "all" ? `?company_id=${selectedId}` : "";
      const { data } = await api.get(`/copilot/daily-summary${cq}`);
      setCopilot(data);
    } catch { setCopilot({ summary: "Le Copilot est indisponible pour le moment.", ok: false }); }
    finally { setCopilotLoading(false); }
  }, [selectedId]);

  useEffect(() => {
    Promise.all([
      api.get("/tickets").then((r) => r.data).catch(() => []),
      api.get("/tasks").then((r) => r.data).catch(() => []),
      api.get("/emails?folder=inbox").then((r) => r.data).catch(() => []),
    ]).then(([tk, ts, em]) => { setTickets(tk); setTasks(ts); setEmails(em || []); });
  }, []);

  const myTickets = tickets.filter((t) => t.assigned_to === me && !["closed", "converted", "auto_closed"].includes(t.status));
  const unassigned = tickets.filter((t) => !t.assigned_to && !["closed", "converted", "auto_closed"].includes(t.status));
  const myTasks = tasks.filter((t) => (t.assigned_to === me || (t.assignees || []).includes(me)) && !["done", "validated", "archived"].includes(t.status));

  const resolvedMine = tasks.filter((t) => (t.assigned_to === me || (t.assignees || []).includes(me)) && (t.resolved_at || t.completed_at || (["done", "validated"].includes(t.status) && t.updated_at)));
  const periodTasks = resolvedMine.filter((t) => inPeriod(t.resolved_at || t.completed_at || t.updated_at, period, range));
  const hours = periodTasks.reduce((a, t) => a + (t.time_spent_seconds || 0), 0) / 3600;
  const gains = periodTasks.reduce((a, t) => a + (t.member_budget || t.member_cost || 0), 0);
  const custom = !!(range.from || range.to);
  const periodLabel = custom ? "période choisie" : PERIODS.find((p) => p[0] === period)[1].toLowerCase();

  const unread = emails.filter((e) => !e.is_read && !e.read).length;

  // 14-day activity = hours worked per day (sum of time spent on tasks resolved that day)
  const series = useMemo(() => {
    const days = [...Array(14)].map((_, i) => { const d = new Date(); d.setDate(d.getDate() - (13 - i)); return d; });
    return days.map((d) => {
      const key = d.toDateString();
      const secs = resolvedMine.filter((t) => { const x = t.resolved_at || t.completed_at || t.updated_at; return x && new Date(x).toDateString() === key; }).reduce((a, t) => a + (t.time_spent_seconds || 0), 0);
      return { d, v: secs / 3600 };
    });
  }, [resolvedMine]);
  const maxV = Math.max(0.5, ...series.map((s) => s.v));

  const W = 640, H = 140, pad = 8;
  const pts = series.map((s, i) => [pad + (i * (W - 2 * pad)) / (series.length - 1), H - pad - (s.v / maxV) * (H - 2 * pad)]);
  const linePath = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L${pts[pts.length - 1][0]},${H} L${pts[0][0]},${H} Z`;

  const greet = new Date().getHours() < 12 ? "Bonjour" : new Date().getHours() < 18 ? "Bon après-midi" : "Bonsoir";

  return (
    <div className="h-full overflow-y-auto bg-background" data-testid="dashboard-home">
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
          <div>
            <p className="font-mono-label text-[11px] uppercase tracking-widest text-klein mb-1">{new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</p>
            <h1 className="font-cabinet font-black text-4xl tracking-tight">{greet}, {(user?.name || "").split(" ")[0]} 👋</h1>
            <p className="text-zinc-400 mt-1 text-sm">Voici un résumé de votre activité.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className={`flex items-center gap-1 bg-card border border-white/10 rounded-full p-1 transition-opacity ${custom ? "opacity-50" : ""}`}>
              {PERIODS.map(([k, l]) => (
                <button key={k} data-testid={`period-${k}`} onClick={() => { setPeriod(k); setRange({ from: "", to: "" }); }} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${period === k && !custom ? "bg-klein text-zinc-950" : "text-zinc-400 hover:text-zinc-200"}`}>{l}</button>
              ))}
            </div>
            <div className={`flex items-center gap-1.5 bg-card border rounded-full px-3 py-1.5 transition-colors ${custom ? "border-klein/50" : "border-white/10"}`} data-testid="period-range">
              <input type="date" data-testid="period-from" value={range.from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className="bg-transparent text-xs focus:outline-none text-foreground w-[112px]" />
              <span className="text-zinc-500 text-xs">→</span>
              <input type="date" data-testid="period-to" value={range.to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="bg-transparent text-xs focus:outline-none text-foreground w-[112px]" />
              {custom && <button data-testid="period-clear" onClick={() => setRange({ from: "", to: "" })} className="text-zinc-400 hover:text-signal ml-0.5"><X size={13} /></button>}
            </div>
          </div>
        </div>

        {/* KPI cards */}
        <OnboardingChecklist onStartTour={() => window.dispatchEvent(new CustomEvent("fd:start-tour"))} />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          <Kpi testid="kpi-tickets" icon={Ticket} color="#3B82F6" label="Tickets à gérer" value={myTickets.length} sub={`${unassigned.length} non assignés`} onClick={() => navigate("/tickets")} delay={0} />
          <Kpi testid="kpi-tasks" icon={ListTodo} color="#10B981" label="Tâches en cours" value={myTasks.length} sub="assignées à vous" onClick={() => navigate("/tasks")} delay={60} />
          <Kpi testid="kpi-unread" icon={Mail} color="#8B5CF6" label="Messages non lus" value={unread} sub="boîte de réception" onClick={() => navigate("/mail")} delay={120} />
          <Kpi testid="kpi-hours" icon={Clock} color="#F59E0B" label="Heures travaillées" value={hours.toFixed(1) + "h"} sub={periodLabel} delay={180} />
        </div>

        {/* Espace client — lien à partager */}
        <ClientSpaceCard companies={companies} />

        {/* Copilot IA */}
        <div className="rounded-2xl border border-klein/20 bg-gradient-to-br from-klein/[0.12] via-klein/[0.04] to-transparent p-5 mb-5 fd-rise" style={{ animationDelay: "210ms" }} data-testid="copilot-card">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-klein/20 text-klein flex items-center justify-center"><Sparkles size={18} /></div>
              <div>
                <h3 className="font-cabinet font-bold text-base leading-tight">Copilot · Brief du jour</h3>
                <p className="text-[11px] text-zinc-500">Résumé intelligent de votre charge de travail</p>
              </div>
            </div>
            <button data-testid="copilot-generate" onClick={loadCopilot} disabled={copilotLoading} className="shrink-0 flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-klein text-white text-sm font-semibold hover:brightness-110 transition disabled:opacity-60">
              {copilotLoading ? <Loader2 size={15} className="animate-spin" /> : copilot ? <RefreshCw size={14} /> : <Sparkles size={15} />}
              {copilotLoading ? "Génération…" : copilot ? "Régénérer" : "Générer mon brief"}
            </button>
          </div>
          {copilotLoading ? (
            <div className="space-y-2 animate-pulse"><div className="h-3 bg-white/10 rounded w-11/12" /><div className="h-3 bg-white/10 rounded w-4/5" /><div className="h-3 bg-white/10 rounded w-3/4" /></div>
          ) : copilot ? (
            <p data-testid="copilot-summary" className="text-sm leading-relaxed text-zinc-200 whitespace-pre-line">{copilot.summary}</p>
          ) : (
            <p className="text-sm text-zinc-400">Cliquez sur « Générer mon brief » pour obtenir un résumé de votre journée et vos priorités, propulsé par l'IA.</p>
          )}
        </div>

        {/* Earnings + chart */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-klein/15 to-transparent p-5 fd-rise" style={{ animationDelay: "240ms" }}>
            <div className="flex items-center gap-2 text-zinc-300 text-sm mb-2"><TrendingUp size={16} className="text-klein" /> Gains estimés</div>
            <div className="font-cabinet font-black text-4xl tabular-nums">{gains.toLocaleString("fr-FR")} <span className="text-xl text-zinc-400">€</span></div>
            <div className="text-xs text-zinc-500 mt-1">{periodTasks.length} tâche(s) · {periodLabel}</div>
          </div>
          <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-card p-5 fd-rise" style={{ animationDelay: "300ms" }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-zinc-300 text-sm"><Flame size={16} className="text-signal" /> Heures travaillées par jour (14 j)</div>
              <span className="font-cabinet font-bold text-sm tabular-nums text-zinc-400">max {maxV.toFixed(1)}h</span>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[140px]" preserveAspectRatio="none">
              <defs>
                <linearGradient id="dashArea" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.35" />
                  <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={areaPath} fill="url(#dashArea)" />
              <path d={linePath} fill="none" stroke="#4F9CFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="fd-draw" pathLength="1" />
              {pts.map((p, i) => <circle key={i} cx={p[0]} cy={p[1]} r="2.5" fill="#4F9CFF" className="fd-fade" style={{ animationDelay: `${600 + i * 50}ms` }} />)}
            </svg>
          </div>
        </div>

        {/* Lists */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ListCard title="Mes tickets prioritaires" testid="dash-tickets" empty="Aucun ticket à gérer. 🎉" onAll={() => navigate("/tickets")}
            items={myTickets.slice(0, 5).map((t) => ({ id: t.id, main: t.subject, sub: t.client_name || t.client_email, badge: t.ref, dot: t.priority === "high" || t.priority === "urgent" ? "#FF3B3B" : "#3B82F6" }))} />
          <ListCard title="Mes tâches en cours" testid="dash-tasks" empty="Aucune tâche en cours." onAll={() => navigate("/tasks")}
            items={myTasks.slice(0, 5).map((t) => ({ id: t.id, main: t.title, sub: t.client_name || t.company_name, badge: t.status, dot: "#10B981" }))} />
        </div>
      </div>
    </div>
  );
}

function ClientSpaceCard({ companies }) {
  const list = (companies || []).filter((c) => c.slug && ["owner", "agent"].includes(c.role));
  const [idx, setIdx] = useState(0);
  if (list.length === 0) return null;
  const c = list[Math.min(idx, list.length - 1)];
  const url = `${window.location.origin}/${c.slug}`;
  const copy = (text, msg) => navigator.clipboard.writeText(text).then(() => toast.success(msg)).catch(() => {});
  const invite = `Bonjour,\n\nAccédez à votre espace client ${c.name} pour suivre vos tickets, vos tâches, valider vos livrables et échanger avec nous :\n${url}\n\nLors de votre première connexion, cliquez sur « Créer mon compte ».\n\nBien cordialement,\nL'équipe ${c.name}`;
  return (
    <div className="rounded-2xl border border-white/10 bg-card p-5 mb-5 fd-rise" style={{ animationDelay: "195ms" }} data-testid="client-space-card">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-9 h-9 rounded-xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center"><Link2 size={18} /></div>
        <div className="flex-1 min-w-0">
          <h3 className="font-cabinet font-bold text-base leading-tight">Espace client</h3>
          <p className="text-[11px] text-zinc-500">Partagez ce lien avec vos clients pour qu'ils suivent leurs projets.</p>
        </div>
        {list.length > 1 && (
          <select data-testid="client-space-company" value={c.id} onChange={(e) => setIdx(list.findIndex((x) => x.id === e.target.value))} className="bg-background border border-white/10 rounded-lg px-2 h-9 text-sm outline-none max-w-[160px]">
            {list.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        )}
      </div>
      <div className="flex items-center gap-2 bg-background border border-white/10 rounded-xl px-3 py-2.5">
        {c.logo_url && <img src={c.logo_url} alt="" className="w-6 h-6 rounded-md object-cover shrink-0" />}
        <code data-testid="client-space-url" className="text-sm text-klein truncate flex-1">{url}</code>
        <button data-testid="client-space-copy" onClick={() => copy(url, "Lien copié")} className="p-1.5 rounded-md hover:bg-white/[0.06] text-zinc-400 hover:text-klein" title="Copier le lien"><Copy size={15} /></button>
        <a data-testid="client-space-open" href={url} target="_blank" rel="noreferrer" className="p-1.5 rounded-md hover:bg-white/[0.06] text-zinc-400 hover:text-klein" title="Ouvrir"><ExternalLink size={15} /></a>
      </div>
      <div className="flex gap-2 mt-3">
        <button data-testid="client-space-invite" onClick={() => copy(invite, "Message d'invitation copié — collez-le dans un email")} className="flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-klein/15 text-klein text-sm font-semibold hover:bg-klein/25"><Send size={14} /> Copier l'invitation par email</button>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, color, label, value, sub, onClick, testid, delay }) {
  return (
    <button data-testid={testid} onClick={onClick} className="text-left rounded-2xl border border-white/10 bg-card p-5 hover:border-white/25 hover:-translate-y-0.5 transition-all fd-rise group" style={{ animationDelay: `${delay}ms` }}>
      <div className="flex items-center justify-between mb-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: color + "22", color }}><Icon size={18} /></div>
        <ArrowRight size={15} className="text-zinc-600 group-hover:text-klein group-hover:translate-x-0.5 transition-all" />
      </div>
      <div className="font-cabinet font-black text-3xl tabular-nums leading-none">{value}</div>
      <div className="text-sm font-medium mt-1.5">{label}</div>
      <div className="text-[11px] text-zinc-500">{sub}</div>
    </button>
  );
}

function ListCard({ title, items, empty, onAll, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-card p-5 fd-rise" style={{ animationDelay: "360ms" }} data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-cabinet font-bold">{title}</h3>
        <button onClick={onAll} className="text-xs text-klein hover:underline flex items-center gap-1">Tout voir <ArrowRight size={12} /></button>
      </div>
      {items.length === 0 ? <p className="text-sm text-zinc-500 py-4 flex items-center gap-2"><CheckCircle2 size={15} className="text-emerald-500" /> {empty}</p> : (
        <div className="space-y-1.5">
          {items.map((it) => (
            <button key={it.id} onClick={onAll} className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl bg-background border border-white/[0.06] hover:border-white/20 transition-colors">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: it.dot }} />
              <div className="min-w-0 flex-1"><div className="text-sm font-medium truncate">{it.main}</div><div className="text-[11px] text-zinc-500 truncate">{it.sub}</div></div>
              <span className="font-mono-label text-[10px] text-zinc-400 shrink-0">{it.badge}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
